import React, { useState, useCallback } from 'react';

const rectsIntersect = (r1: DOMRect, r2: {x:number,y:number,w:number,h:number}) => {
  return !(r2.x > r1.right || (r2.x + r2.w) < r1.left || r2.y > r1.bottom || (r2.y + r2.h) < r1.top);
};

export function useDragSelection(
  gridRef: React.RefObject<HTMLDivElement | null>,
  selectedAssetIds: string[],
  onSelectedAssetsChange: (ids: string[]) => void,
  onSelectionAnchorChange: (id: string | null) => void,
  suppressClickRef: React.RefObject<number>
) {
  const [isDraggingSelect, setIsDraggingSelect] = useState(false);
  const dragStartRef = React.useRef<{x:number,y:number}|null>(null);
  const dragRectRef = React.useRef<{x:number,y:number,w:number,h:number}|null>(null);
  const dragBoxRef = React.useRef<HTMLDivElement|null>(null);
  const rafRef = React.useRef<number|null>(null);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    if (Date.now() - suppressClickRef.current < 400) return;
    if ((e.target as HTMLElement).closest('[data-asset-card]')) return;

    dragStartRef.current = { x: e.clientX, y: e.clientY };
    dragRectRef.current = { x: e.clientX, y: e.clientY, w: 0, h: 0 };
    setIsDraggingSelect(true);

    if (dragBoxRef.current) {
      dragBoxRef.current.style.left = `${e.clientX}px`;
      dragBoxRef.current.style.top = `${e.clientY}px`;
      dragBoxRef.current.style.width = '0px';
      dragBoxRef.current.style.height = '0px';
    }
  }, [suppressClickRef]);

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    if (!dragStartRef.current) return;

    const startX = dragStartRef.current.x;
    const startY = dragStartRef.current.y;
    const clientX = e.clientX;
    const clientY = e.clientY;

    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
    }

    rafRef.current = requestAnimationFrame(() => {
      const x = Math.min(clientX, startX);
      const y = Math.min(clientY, startY);
      const w = Math.abs(clientX - startX);
      const h = Math.abs(clientY - startY);

      dragRectRef.current = { x, y, w, h };

      if (dragBoxRef.current) {
        dragBoxRef.current.style.left = `${x}px`;
        dragBoxRef.current.style.top = `${y}px`;
        dragBoxRef.current.style.width = `${w}px`;
        dragBoxRef.current.style.height = `${h}px`;
      }
    });
  }, []);

  const handleMouseUp = useCallback(() => {
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }

    if (!dragStartRef.current || !dragRectRef.current) {
      setIsDraggingSelect(false);
      dragStartRef.current = null;
      dragRectRef.current = null;
      return;
    }

    const container = gridRef.current;
    if (!container) {
      setIsDraggingSelect(false);
      dragStartRef.current = null;
      dragRectRef.current = null;
      return;
    }

    const cards = Array.from(container.querySelectorAll('[data-asset-card]')) as HTMLElement[];
    const ids: string[] = [];
    const rect = dragRectRef.current;
    const isClick = rect.w < 3 && rect.h < 3;

    cards.forEach(card => {
      const r = card.getBoundingClientRect();
      if (rectsIntersect(r, rect)) {
        const id = card.getAttribute('data-asset-id');
        if (id) ids.push(id);
      }
    });

    if (ids.length > 0) {
      const unique = Array.from(new Set([ ...selectedAssetIds, ...ids ]));
      onSelectedAssetsChange(unique);
      onSelectionAnchorChange(ids[ids.length - 1] || null);
    } else if (isClick && selectedAssetIds.length > 0) {
      onSelectedAssetsChange([]);
      onSelectionAnchorChange(null);
    }

    setIsDraggingSelect(false);
    dragStartRef.current = null;
    dragRectRef.current = null;
  }, [gridRef, selectedAssetIds, onSelectedAssetsChange, onSelectionAnchorChange]);

  return {
    isDraggingSelect,
    dragBoxRef,
    handleMouseDown,
    handleMouseMove,
    handleMouseUp,
  };
}
